import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import { Vehicle } from '../models/vehicle.model';
import { normalizeApiArray } from '../utils/api-response.util';

@Injectable({
  providedIn: 'root'
})
export class VehicleService {
  private apiUrl = `${environment.apiUrl}/Vehicle`;

  constructor(private http: HttpClient) { }

  // Get all vehicles
  getVehicles(organisationId?: number): Observable<Vehicle[]> {
    return this.http.get<Vehicle[] | { $values?: Vehicle[] }>(this.apiUrl).pipe(
      map(response => {
        const vehicles = normalizeApiArray<Vehicle>(response);
        return organisationId
          ? vehicles.filter(vehicle => vehicle.organisationID === organisationId)
          : vehicles;
      })
    );
  }

  // Get vehicle by ID
  getVehicleById(id: number): Observable<Vehicle> {
    return this.http.get<Vehicle>(`${this.apiUrl}/${id}`);
  }

  // Create new vehicle
  createVehicle(vehicleData: Partial<Vehicle>): Observable<Vehicle> {
    return this.http.post<Vehicle>(this.apiUrl, vehicleData);
  }

  // Update vehicle
  updateVehicle(id: number, vehicleData: Partial<Vehicle>): Observable<Vehicle> {
    return this.http.put<Vehicle>(`${this.apiUrl}/${id}`, vehicleData);
  }

  // Delete vehicle
  deleteVehicle(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  // Assign device to vehicle
  assignDevice(vehicleId: number, deviceId: number): Observable<Vehicle> {
    return this.http.patch<Vehicle>(`${this.apiUrl}/${vehicleId}/assign-device`, { deviceId });
  }

  // Remove device from vehicle
  removeDevice(vehicleId: number): Observable<Vehicle> {
    return this.http.patch<Vehicle>(`${this.apiUrl}/${vehicleId}/remove-device`, {});
  }

  // Get available vehicles (not assigned to any trip in a given time period)
  getAvailableVehicles(startTime: string, endTime: string): Observable<Vehicle[]> {
    return this.http.post<Vehicle[]>(`${this.apiUrl}/available`, { startTime, endTime });
  }

  // Get vehicle maintenance history
  getMaintenanceHistory(vehicleId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${vehicleId}/maintenance`);
  }

  // Add maintenance record
  addMaintenanceRecord(vehicleId: number, maintenanceData: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${vehicleId}/maintenance`, maintenanceData);
  }

  // Get vehicle statistics
  getVehicleStats(): Observable<{
    totalVehicles: number;
    activeVehicles: number;
    vehiclesWithDevice: number;
    vehiclesWithoutDevice: number;
  }> {
    return this.http.get<any>(`${this.apiUrl}/stats`);
  }

  // Search vehicles
  searchVehicles(query: string): Observable<Vehicle[]> {
    return this.http.get<Vehicle[]>(`${this.apiUrl}/search`, {
      params: { query }
    });
  }

  // Bulk update vehicle status
  bulkUpdateStatus(vehicleIds: number[], status: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/bulk-status`, { vehicleIds, status });
  }

  // Import vehicles from CSV/Excel
  importVehicles(file: File): Observable<{ imported: number; failed: number }> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.post<{ imported: number; failed: number }>(
      `${this.apiUrl}/import`,
      formData
    );
  }

  // Export vehicles to CSV/Excel
  exportVehicles(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/export`, {
      responseType: 'blob'
    });
  }
}
